import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdminBackfillPanel, AdminSyncPanel, AdminUsersSection } from "@/components/admin/AdminDashboardSections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [createEmail, setCreateEmail] = useState("");
  const [createLevel, setCreateLevel] = useState("stripe");

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncJobs, setSyncJobs] = useState([]);

  // Yield curve status
  const [yieldCurve, setYieldCurve] = useState(null);
  const [latestReportDate, setLatestReportDate] = useState(null);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);

  // Liquidation hourly job toggle
  const [liqHourlyEnabled, setLiqHourlyEnabled] = useState(true);

  const canView = useMemo(() => me?.role === "admin", [me]);
  const syncHealth = useMemo(() => {
    const cooldownCount = syncStatus?.ai_cooldown_symbols?.length || 0;
    const rateLimitFails = syncStatus?.ai_rate_limit_failures || 0;
    const status = (cooldownCount > 0 || rateLimitFails > 0) ? "Degraded" : "Healthy";
    return { cooldownCount, rateLimitFails, status };
  }, [syncStatus]);

  const yieldCurveStatus = useMemo(() => {
    if (!yieldCurve) {
      return {
        label: "Unknown",
        tone: "border-slate-200 bg-slate-50 text-slate-600",
        value: "—",
        date: "No data",
        streakLabel: "Inversion streak",
        streakValue: "N/A",
        inversionDateLabel: "Last inversion",
        inversionDateValue: "N/A",
      };
    }

    const latestValue = Number(yieldCurve.latest_value);
    const valueText = Number.isFinite(latestValue) ? `${latestValue.toFixed(2)}%` : "N/A";
    const dateText = yieldCurve.latest_date || "N/A";

    const inversionDaysRaw = yieldCurve.is_inverted ? yieldCurve.inversion_days : yieldCurve.last_inversion_days;
    const inversionDays = Number(inversionDaysRaw);
    const streakValue = Number.isFinite(inversionDays) && inversionDays > 0 ? `${inversionDays} days` : "N/A";

    const hasStartDate = !!yieldCurve.last_inversion_start;
    const inversionStart = hasStartDate
      ? yieldCurve.last_inversion_start
      : (yieldCurve.is_inverted ? yieldCurve.latest_date : yieldCurve.last_inversion_end);
    const inversionDateValue = inversionStart || "N/A";

    const streakLabel = yieldCurve.is_inverted ? "Inversion streak" : "Last inversion streak";
    let inversionDateLabel = yieldCurve.is_inverted
      ? (hasStartDate ? "Inversion started" : "Inversion ongoing")
      : (hasStartDate ? "Last inversion started" : "Last inversion ended");
    if (inversionDateValue === "N/A") {
      inversionDateLabel = "Last inversion";
    }

    const base = {
      value: `10Y–3M ${valueText}`,
      date: `Latest ${dateText}`,
      streakLabel,
      streakValue,
      inversionDateLabel,
      inversionDateValue,
    };

    if (yieldCurve.is_inverted) {
      return {
        label: "Inverted",
        tone: "border-rose-200 bg-rose-50 text-rose-700",
        ...base,
      };
    }

    if (yieldCurve.re_steepened) {
      return {
        label: "Re-steepening",
        tone: "border-amber-200 bg-amber-50 text-amber-700",
        ...base,
      };
    }

    return {
      label: "Normal",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      ...base,
    };
  }, [yieldCurve]);

  const fetchMe = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/me`, { withCredentials: true });
      setMe(resp.data);
      setAuthChecked(true);
      return resp.data;
    } catch (err) {
      setAuthChecked(true);
      return null;
    }
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync/status`, { withCredentials: true });
      setSyncStatus(response.data);
      const active = response.data?.status === "syncing" || response.data?.status === "queued";
      setSyncing(active);
      return response.data;
    } catch (e) {
      return null;
    }
  }, []);

  const fetchSyncJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/sync/jobs`, { withCredentials: true });
      setSyncJobs(response.data?.jobs || []);
    } catch {
      setSyncJobs([]);
    }
  }, []);

  const fetchYieldCurve = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/macro/yield-curve`, { withCredentials: true });
      setYieldCurve(response.data?.yield_curve || null);
    } catch (e) {
      setYieldCurve(null);
    }
  }, []);

  const fetchLatestReportDate = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/latest`, { withCredentials: true });
      setLatestReportDate(response.data?.email_date || null);
    } catch (e) {
      setLatestReportDate(null);
    }
  }, []);

  const fetchBackfillStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/price-backfill/status`, { withCredentials: true });
      setBackfillStatus(Object.keys(response.data || {}).length ? response.data : null);
    } catch (e) {
      setBackfillStatus(null);
    }
  }, []);

  const fetchLiqHourly = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/liquidation-hourly`, { withCredentials: true });
      setLiqHourlyEnabled(!!resp.data?.enabled);
    } catch {
      setLiqHourlyEnabled(true);
    }
  }, []);

  const setLiqHourly = async (enabled) => {
    try {
      await axios.post(`${API}/admin/liquidation-hourly`, { enabled }, { withCredentials: true });
      setLiqHourlyEnabled(enabled);
      toast.success(`Hourly liquidation snapshots ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error("Failed to update liquidation scheduler");
    }
  };

  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      await axios.post(`${API}/sync`, {}, { withCredentials: true });
      toast.info("Sync queued...");
      fetchSyncStatus();
      fetchSyncJobs();
    } catch (e) {
      toast.error("Failed to refresh data");
      setSyncing(false);
    }
  }, [fetchSyncJobs, fetchSyncStatus, syncing]);

  const handleBackfill = async () => {
    try {
      setBackfillRunning(true);
      toast.info("Starting 5-year price backfill...");
      await axios.post(`${API}/admin/price-backfill`, { lookback_days: 1825 }, { withCredentials: true });
      toast.success("Price backfill completed");
      fetchBackfillStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run price backfill");
    } finally {
      setBackfillRunning(false);
    }
  };

  // Poll sync status while syncing
  useEffect(() => {
    let interval;
    if (syncing) {
      interval = setInterval(async () => {
        const status = await fetchSyncStatus();
        fetchSyncJobs();
        if (status && status.status !== "syncing" && status.status !== "queued") {
          setSyncing(false);
          if (status.status === "completed") {
            toast.success(status.message || "Sync completed successfully");
            fetchYieldCurve();
            fetchLatestReportDate();
          } else if (status.status === "error") {
            toast.error(status.message || "Sync failed");
          }
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [syncing, fetchSyncStatus, fetchSyncJobs, fetchYieldCurve, fetchLatestReportDate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
        params: search ? { search } : {},
      });
      setUsers(resp.data?.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const init = async () => {
      const userData = await fetchMe();
      // If not logged in or not admin, redirect to login
      if (!userData || !userData.id) {
        navigate("/login");
        return;
      }
      if (userData.role !== "admin") {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      // Fetch sync status for admin
      fetchSyncStatus();
      fetchSyncJobs();
      fetchLiqHourly();
      fetchYieldCurve();
      fetchLatestReportDate();
      fetchBackfillStatus();
    };
    init();
  }, [navigate, fetchMe, fetchSyncStatus, fetchSyncJobs, fetchLiqHourly, fetchYieldCurve, fetchLatestReportDate, fetchBackfillStatus]);

  useEffect(() => {
    if (canView) fetchUsers();
  }, [canView, fetchUsers]);

  const createUser = async () => {
    try {
      await axios.post(
        `${API}/admin/users`,
        { email: createEmail, accessLevel: createLevel },
        { withCredentials: true },
      );
      toast.success("User created and invite email sent");
      setCreateEmail("");
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    }
  };

  const updateUser = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/users/${id}`, patch, { withCredentials: true });
      await fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update user");
    }
  };

  const resendInvite = async (id) => {
    try {
      await axios.post(`${API}/admin/users/${id}/resend-invite`, {}, { withCredentials: true });
      toast.success("Invite resent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend invite");
    }
  };

  const sendPasswordReset = async (id) => {
    try {
      await axios.post(`${API}/admin/users/${id}/send-password-reset`, {}, { withCredentials: true });
      toast.success("Password reset email sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send password reset");
    }
  };

  if (!authChecked) {
    return <div className="p-6 text-sm text-slate-600">Loading…</div>;
  }

  if (!me || !me.id) {
    return <div className="p-6 text-sm text-slate-600">Redirecting to login...</div>;
  }

  if (!canView) {
    return <div className="p-6 text-sm text-slate-600">Admin only.</div>;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const getSyncMessage = (fallback) => {
    const msg = syncStatus?.message;
    if (!msg) return fallback;
    if (String(msg).toLowerCase().includes("successfully synced")) {
      return fallback;
    }
    return msg;
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      {/* Admin Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-500">Manage users and system settings</p>
          </div>
        </div>
        <Button
          variant="outline"
          data-testid="admin-back-to-dashboard-button"
          onClick={() => navigate("/app")}
        >
          Back to Dashboard
        </Button>
      </div>

      <AdminSyncPanel
        syncHealth={syncHealth}
        yieldCurveStatus={yieldCurveStatus}
        latestReportDate={latestReportDate}
        formatDate={formatDate}
        syncStatus={syncStatus}
        syncing={syncing}
        getSyncMessage={getSyncMessage}
        handleSync={handleSync}
        syncJobs={syncJobs}
        liqHourlyEnabled={liqHourlyEnabled}
        setLiqHourly={setLiqHourly}
      />

      <AdminBackfillPanel
        backfillStatus={backfillStatus}
        backfillRunning={backfillRunning}
        handleBackfill={handleBackfill}
        formatDate={formatDate}
      />

      <AdminUsersSection
        search={search}
        setSearch={setSearch}
        fetchUsers={fetchUsers}
        loading={loading}
        createEmail={createEmail}
        setCreateEmail={setCreateEmail}
        createLevel={createLevel}
        setCreateLevel={setCreateLevel}
        createUser={createUser}
        users={users}
        updateUser={updateUser}
        resendInvite={resendInvite}
        sendPasswordReset={sendPasswordReset}
      />
    </div>
  );
}
